import { z } from "zod";

export const AI_SUMMARY_STEPS = [
    "review_themes",
    "fact_extraction",
    "normalize_evidence",
    "profile_summary",
    "reviews_summary",
] as const;

export const AiSummaryStepSchema = z.enum(AI_SUMMARY_STEPS);
export const AiStepRunStatusSchema = z.enum(["pending", "running", "succeeded", "failed", "skipped"]);
export const AiStepRunTriggerSchema = z.enum(["manual", "console", "cascade", "partner_sync"]);
export const AiSummaryKindSchema = z.enum(["profile", "reviews"]);

const AiActorSchema = z.object({
    id: z.number(),
    name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
});

export const ExpertAiContextSchema = z.object({
    expert_id: z.number(),
    additional_public_data: z.string().nullable(),
    admin_context: z.string().nullable(),
    must_include: z.string().nullable(),
    must_avoid: z.string().nullable(),
    updated_by: AiActorSchema.nullable(),
    updated_at: z.string().nullable(),
});

export const UpdateExpertAiContextRequestSchema = z.object({
    additional_public_data: z.string().nullable().optional(),
    admin_context: z.string().nullable().optional(),
    must_include: z.string().nullable().optional(),
    must_avoid: z.string().nullable().optional(),
});

export const ExpertAiStepRunUsageSchema = z.object({
    prompt_tokens: z.number().nullable(),
    completion_tokens: z.number().nullable(),
    cost: z.number().nullable(),
});

export const ExpertAiStepRunSchema = z.object({
    id: z.number(),
    expert_id: z.number(),
    step: AiSummaryStepSchema,
    revision: z.number(),
    status: AiStepRunStatusSchema,
    trigger: AiStepRunTriggerSchema,
    triggered_by: AiActorSchema.nullable(),
    parent_run_id: z.number().nullable(),
    model: z.string().nullable(),
    prompt_version: z.string().nullable(),
    usage: ExpertAiStepRunUsageSchema,
    error: z.string().nullable(),
    started_at: z.string().nullable(),
    finished_at: z.string().nullable(),
    created_at: z.string(),
});

export const ExpertAiStepRunDetailSchema = ExpertAiStepRunSchema.extend({
    input_snapshot: z.unknown().nullable(),
    output: z.unknown().nullable(),
    raw_response: z.unknown().nullable(),
});

export const ExpertAiSummaryStructuredSchema = z.object({
    summary: z.string().optional(),
    recommended_for: z.array(z.string()).optional(),
    not_ideal_for: z.array(z.string()).optional(),
    most_common_praise: z.array(z.string()).optional(),
    most_common_concerns: z.array(z.string()).optional(),
    review_count: z.number().optional(),
    average_rating: z.number().nullable().optional(),
}).passthrough();

export const ExpertAiCurrentSummarySchema = z.object({
    content: z.string(),
    structured: ExpertAiSummaryStructuredSchema.nullable(),
    run_id: z.number(),
    revision: z.number(),
    published_at: z.string().nullable(),
});

const ExpertAiStepOverviewSchema = z.object({
    dependencies: z.array(AiSummaryStepSchema),
    latest_run: ExpertAiStepRunSchema.nullable(),
});

export const ExpertAiSummaryOverviewSchema = z.object({
    expert_id: z.number(),
    summaries: z.object({
        profile: ExpertAiCurrentSummarySchema.nullable(),
        reviews: ExpertAiCurrentSummarySchema.nullable(),
    }),
    steps: z.object({
        review_themes: ExpertAiStepOverviewSchema,
        fact_extraction: ExpertAiStepOverviewSchema,
        normalize_evidence: ExpertAiStepOverviewSchema,
        profile_summary: ExpertAiStepOverviewSchema,
        reviews_summary: ExpertAiStepOverviewSchema,
    }),
});

export const ExpertAiStepRunListMetaSchema = z.object({
    total: z.number().optional(),
}).passthrough();

export const ExpertAiTriggerResponseSchema = z.object({
    runs: z.array(ExpertAiStepRunSchema),
});

export const RegenerateExpertAiStepRequestSchema = z.object({
    cascade: z.boolean().optional(),
});

export const PublishExpertAiSummaryRequestSchema = z.object({
    run_id: z.number().int().min(1),
});

export const ExpertAiPublishedSummarySchema = ExpertAiCurrentSummarySchema.extend({
    kind: AiSummaryKindSchema,
});

export const ExpertProfileAiProfileSummarySchema = z.object({
    content: z.string(),
    recommended_for: z.array(z.string()).default([]),
    not_ideal_for: z.array(z.string()).default([]),
    generated_at: z.string().nullable().optional(),
});

export const ExpertProfileAiReviewsSummarySchema = z.object({
    content: z.string(),
    most_common_praise: z.array(z.string()).default([]),
    most_common_concerns: z.array(z.string()).default([]),
    review_count: z.number().nullable().optional(),
    generated_at: z.string().nullable().optional(),
});

/** `null` as a whole while the feature is switched off; each kind is `null` until something is published. */
export const ExpertProfileAiSummariesSchema = z.object({
    profile: ExpertProfileAiProfileSummarySchema.nullable(),
    reviews: ExpertProfileAiReviewsSummarySchema.nullable(),
});

export type AiSummaryStep = z.infer<typeof AiSummaryStepSchema>;
export type AiStepRunStatus = z.infer<typeof AiStepRunStatusSchema>;
export type AiStepRunTrigger = z.infer<typeof AiStepRunTriggerSchema>;
export type AiSummaryKind = z.infer<typeof AiSummaryKindSchema>;
export type ExpertAiContext = z.infer<typeof ExpertAiContextSchema>;
export type UpdateExpertAiContextRequest = z.input<typeof UpdateExpertAiContextRequestSchema>;
export type ExpertAiStepRun = z.infer<typeof ExpertAiStepRunSchema>;
export type ExpertAiStepRunDetail = z.infer<typeof ExpertAiStepRunDetailSchema>;
export type ExpertAiSummaryStructured = z.infer<typeof ExpertAiSummaryStructuredSchema>;
export type ExpertAiCurrentSummary = z.infer<typeof ExpertAiCurrentSummarySchema>;
export type ExpertAiSummaryOverview = z.infer<typeof ExpertAiSummaryOverviewSchema>;
export type ExpertAiStepRunListMeta = z.infer<typeof ExpertAiStepRunListMetaSchema>;
export type ExpertAiTriggerResponse = z.infer<typeof ExpertAiTriggerResponseSchema>;
export type RegenerateExpertAiStepRequest = z.input<typeof RegenerateExpertAiStepRequestSchema>;
export type PublishExpertAiSummaryRequest = z.input<typeof PublishExpertAiSummaryRequestSchema>;
export type ExpertAiPublishedSummary = z.infer<typeof ExpertAiPublishedSummarySchema>;
export type ExpertProfileAiProfileSummary = z.infer<typeof ExpertProfileAiProfileSummarySchema>;
export type ExpertProfileAiReviewsSummary = z.infer<typeof ExpertProfileAiReviewsSummarySchema>;
export type ExpertProfileAiSummaries = z.infer<typeof ExpertProfileAiSummariesSchema>;

export const GenerateExpertAiBatchRequestSchema = z.object({
    expert_ids: z.array(z.number().int().positive()).min(1).max(100)
        .refine((ids) => new Set(ids).size === ids.length, 'Expert IDs must be distinct'),
    only_missing: z.boolean().optional(),
});

export const ExpertAiBatchTriggerResponseSchema = z.object({
    batch_id: z.string().uuid().nullable(),
    status: z.enum(['queued', 'completed']),
    total: z.number().int().nonnegative(),
    skipped_expert_ids: z.array(z.number().int()),
});

export const ExpertAiBatchStatusSchema = z.object({
    batch_id: z.string().uuid(),
    status: z.enum(['queued', 'running', 'completed', 'failed']),
    completed: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    experts: z.array(z.object({
        expert_id: z.number().int(),
        status: z.enum(['queued', 'running', 'completed', 'failed']),
        steps: z.array(z.object({
            run_id: z.number().int(),
            step: AiSummaryStepSchema,
            status: AiStepRunStatusSchema,
            error: z.string().nullable(),
        })),
    })),
});
export type ExpertAiBatchStatus = z.infer<typeof ExpertAiBatchStatusSchema>;
